import type { Guide, GuideScope, GuideSelection } from "@/document/model/guides";
import type { OklchColor } from "@/document/model/color";
import type { Rect } from "@/lib/math/rect";
import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";
import {
  drawFrame,
  drawPrimitive,
  dropImageOnCanvas,
  reorderFlexChild,
  selectNodes,
  selectPrototypeLink,
  updateGradientPaint,
  updateNodeBounds,
  updateNodeTransforms,
  updatePrimitiveDefinition,
  updateTextCharacters,
  toggleTextBold,
} from "@/editor/commands";
import { bindCommand } from "@/editor/shell/shellCommandBinding";
import type { DocumentCanvasActions } from "./types";

export interface DocumentActionDeps {
  getActivePageId: () => string | undefined;
  onBoundsPreviewChange: (bounds: Rect | undefined) => void;
  onRotationOriginCommit: DocumentCanvasActions["onRotationOriginCommit"];
  onDuplicateDrag: DocumentCanvasActions["onDuplicateDrag"];
  onContextMenu: DocumentCanvasActions["onContextMenu"];
  commitColorSample: DocumentCanvasActions["onColorSample"];
  onComponentPlacementEnd?: DocumentCanvasActions["onComponentPlacementEnd"];
  onComponentPlacementCommit?: DocumentCanvasActions["onComponentPlacementCommit"];
  onGuideSelectionChange: DocumentCanvasActions["onGuideSelectionChange"];
  onGuideAdd: DocumentCanvasActions["onGuideAdd"];
  onGuideMove: DocumentCanvasActions["onGuideMove"];
  onGuideRelocate: DocumentCanvasActions["onGuideRelocate"];
  onGuideDelete: DocumentCanvasActions["onGuideDelete"];
  onPrototypeLinkCreate: DocumentCanvasActions["onPrototypeLinkCreate"];
  onRenameNode: DocumentCanvasActions["onRenameNode"];
  onTextInsert: DocumentCanvasActions["onTextInsert"];
  onTextEditExit: DocumentCanvasActions["onTextEditExit"];
  onTextSelectionChange: DocumentCanvasActions["onTextSelectionChange"];
  onEnterTextMode: DocumentCanvasActions["onEnterTextMode"];
  onScriptEntryDrop?: DocumentCanvasActions["onScriptEntryDrop"];
  onEditFrameScript?: DocumentCanvasActions["onEditFrameScript"];
  onRunFrameScript?: DocumentCanvasActions["onRunFrameScript"];
}

export function createDocumentActions(host: EditorCommandHost, deps: DocumentActionDeps): DocumentCanvasActions {
  const { getActivePageId } = deps;
  return {
    onSelectionChange: (nodeIds, primaryNodeId) => selectNodes(host, nodeIds, primaryNodeId),
    onUndo: () => host.undo(),
    onRedo: () => host.redo(),
    onShapeDraw: (kind, bounds, parentFrameId) => drawPrimitive(host, kind, bounds, parentFrameId, { getActivePageId }),
    onFrameDraw: (bounds, parentFrameId) => drawFrame(host, bounds, parentFrameId, undefined, { getActivePageId }),
    onBoundsCommit: (updates, options) => updateNodeBounds(host, updates, "Updated bounds", options),
    onFlexReorderCommit: bindCommand(host, reorderFlexChild),
    onBoundsPreviewChange: deps.onBoundsPreviewChange,
    onTransformCommit: updates => updateNodeTransforms(host, updates, "Updated rotation"),
    onRotationOriginCommit: deps.onRotationOriginCommit,
    onDuplicateDrag: deps.onDuplicateDrag,
    onGradientPaintChange: bindCommand(host, updateGradientPaint),
    onPrimitiveChange: bindCommand(host, updatePrimitiveDefinition),
    onImageFileDrop: bindCommand(host, dropImageOnCanvas),
    onScriptEntryDrop: deps.onScriptEntryDrop,
    onEditFrameScript: deps.onEditFrameScript,
    onRunFrameScript: deps.onRunFrameScript,
    onContextMenu: deps.onContextMenu,
    onColorSample: deps.commitColorSample,
    onComponentPlacementEnd: deps.onComponentPlacementEnd,
    onComponentPlacementCommit: deps.onComponentPlacementCommit,
    onGuideSelectionChange: deps.onGuideSelectionChange,
    onGuideAdd: deps.onGuideAdd,
    onGuideMove: deps.onGuideMove,
    onGuideRelocate: deps.onGuideRelocate,
    onGuideDelete: deps.onGuideDelete,
    onPrototypeLinkCreate: deps.onPrototypeLinkCreate,
    onPrototypeLinkSelect: bindCommand(host, selectPrototypeLink),
    onRenameNode: deps.onRenameNode,
    onTextInsert: deps.onTextInsert,
    onTextEditExit: deps.onTextEditExit,
    onTextCharactersChange: bindCommand(host, updateTextCharacters),
    onTextBoldShortcut: bindCommand(host, toggleTextBold),
    onTextSelectionChange: deps.onTextSelectionChange,
    onEnterTextMode: deps.onEnterTextMode,
  };
}
